<template>
  <div class="photo-gallery">
    <div v-if="!hideStrip && items.length" class="photo-gallery__strip">
      <button
        v-for="(item, i) in items"
        :key="item.id"
        type="button"
        class="photo-gallery__thumb"
        :style="{ width: size + 'px', height: size + 'px' }"
        :aria-label="`Deschide fotografia ${i + 1}`"
        @click="open(i)"
      >
        <auth-image :file-id="item.id" :alt="`Fotografia ${i + 1}`" />
      </button>
    </div>

    <v-dialog :value="viewer" fullscreen transition="fade-transition" @input="v => !v && close()">
      <div class="viewer" :class="{ 'viewer--animate': animate }">
        <div class="viewer__bar">
          <v-btn icon dark large aria-label="Închide" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <span v-if="items.length > 1" class="viewer__count">{{ index + 1 }} / {{ items.length }}</span>
          <v-spacer />
          <v-btn text dark large :loading="downloading" :disabled="!current" @click="download">
            <v-icon left>mdi-download</v-icon>
            Descarcă
          </v-btn>
        </div>

        <div
          ref="stage"
          class="viewer__stage"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
          @wheel.prevent="onWheel"
          @dblclick="onDoubleClick"
        >
          <div class="viewer__track" :style="{ transform: `translate3d(${dragX}px, 0, 0)` }">
            <div
              v-for="slide in slides"
              :key="slide.key"
              class="viewer__slide"
              :style="{ transform: `translate3d(${slide.offset * 100}%, 0, 0)` }"
            >
              <img
                v-if="slide.url"
                :src="slide.url"
                :alt="`Fotografia ${slide.index + 1}`"
                draggable="false"
                :style="slide.style"
                @load="onImageLoad(slide, $event)"
              />
              <v-progress-circular v-if="slide.loading" class="viewer__spinner" indeterminate color="white" size="36" width="3" />
              <div v-if="slide.offset === 0 && failed" class="viewer__error">
                <v-icon dark large>mdi-image-off-outline</v-icon>
                <div>{{ failed }}</div>
              </div>
            </div>
          </div>
        </div>

        <template v-if="items.length > 1">
          <v-btn v-show="index > 0" class="viewer__nav viewer__nav--prev" fab small aria-label="Fotografia anterioară" @click="go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn v-show="index < items.length - 1" class="viewer__nav viewer__nav--next" fab small aria-label="Fotografia următoare" @click="go(1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </template>
      </div>
    </v-dialog>
  </div>
</template>

<script>
// Thumbnail strip + fullscreen viewer: swipe between photos, pinch / double-tap / wheel zoom, drag to pan
import AuthImage from "./AuthImage.vue";
import { acquirePhoto, releasePhoto, downloadPhoto } from "./photoCache";
import { photoErrorMessage } from "./api";

const MAX_SCALE = 6;
const DOUBLE_TAP_ZOOM = 2.5;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default {
  name: "PhotoGallery",
  components: { AuthImage },
  props: {
    // [{ id, width, height }] (or plain ids)
    files: { type: Array, default: () => [] },
    size: { type: Number, default: 72 },
    // only the viewer (opened by the parent with ref.open(i))
    hideStrip: { type: Boolean, default: false },
    // file name prefix for „Descarcă”
    downloadName: { type: String, default: "fotografie" },
  },
  data() {
    return {
      viewer: false,
      index: 0,
      urls: {}, // "variant:id" -> object URL
      dims: {}, // id -> { w, h } when the payload had none
      stageW: 0,
      stageH: 0,
      scale: 1,
      tx: 0,
      ty: 0,
      dragX: 0,
      animate: false,
      downloading: false,
      failed: "",
    };
  },
  computed: {
    items() {
      return (this.files || [])
        .map(f => (f && typeof f === "object" ? f : { id: f }))
        .filter(f => f.id != null);
    },
    current() {
      return this.items[this.index] || null;
    },
    slides() {
      const out = [];
      for (let offset = -1; offset <= 1; offset++) {
        const item = this.items[this.index + offset];
        if (!item) continue;
        const full = offset === 0 ? this.urls[`full:${item.id}`] : null;
        const thumb = this.urls[`thumb:${item.id}`];
        const fit = this.fitOf(item);
        const style = { width: `${fit.w}px`, height: `${fit.h}px` };
        if (offset === 0) style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
        out.push({
          key: item.id,
          index: this.index + offset,
          offset,
          item,
          url: full || thumb || null,
          isFull: !!full,
          loading: offset === 0 && !full && !this.failed,
          style,
        });
      }
      return out;
    },
  },
  watch: {
    files() {
      if (this.viewer && this.index >= this.items.length) this.index = Math.max(0, this.items.length - 1);
      if (this.viewer && !this.items.length) this.close();
      else if (this.viewer) this.syncHeld();
    },
  },
  beforeDestroy() {
    this.stopListening();
    this.releaseAll();
  },
  methods: {
    /** Opens the viewer at photo `i` */
    open(i = 0) {
      if (!this.items.length) return;
      this.index = clamp(i, 0, this.items.length - 1);
      this.resetZoom(false);
      this.viewer = true;
      this.$emit("open", this.index);
      this.startListening();
      this.$nextTick(() => {
        this.syncHeld();
        this.watchStage();
      });
    },
    close() {
      if (!this.viewer) return;
      this.viewer = false;
      this.stopListening();
      this.releaseAll();
      this.$emit("close");
    },
    startListening() {
      if (this.listening) return;
      this.listening = true;
      this.onResize = () => this.measure();
      this.onKey = e => {
        if (e.key === "ArrowLeft") this.go(-1);
        else if (e.key === "ArrowRight") this.go(1);
      };
      window.addEventListener("resize", this.onResize);
      window.addEventListener("keydown", this.onKey);
    },
    stopListening() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.resizeObserver = null;
      cancelAnimationFrame(this.measureFrame);
      if (!this.listening) return;
      this.listening = false;
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("keydown", this.onKey);
    },
    /** The dialog lays out a moment after it opens: measure once it has a size, and on every resize */
    watchStage(tries = 0) {
      const stage = this.$refs.stage;
      if (!this.viewer) return;
      if (stage && typeof ResizeObserver === "function" && !this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(stage);
      }
      this.measure();
      if ((!this.stageW || !this.stageH) && tries < 60) {
        this.measureFrame = requestAnimationFrame(() => this.watchStage(tries + 1));
      }
    },
    measure() {
      const stage = this.$refs.stage;
      if (!stage || !stage.clientWidth || !stage.clientHeight) return;
      this.stageW = stage.clientWidth;
      this.stageH = stage.clientHeight;
      this.clampPan();
    },

    // ---- object URLs: the current photo in full, neighbours as thumbnails
    syncHeld() {
      if (!this.held) this.held = new Map();
      const wanted = new Set();
      const cur = this.current;
      if (cur) wanted.add(`full:${cur.id}`);
      [-1, 0, 1].forEach(o => {
        const item = this.items[this.index + o];
        if (item) wanted.add(`thumb:${item.id}`);
      });
      this.held.forEach((ref, key) => {
        if (!wanted.has(key)) {
          releasePhoto(ref.id, ref.variant);
          this.held.delete(key);
          this.$delete(this.urls, key);
        }
      });
      this.failed = "";
      wanted.forEach(key => {
        if (this.held.has(key)) return;
        const [variant, rawId] = key.split(":");
        const item = this.items.find(i => String(i.id) === rawId);
        const ref = { id: item.id, variant };
        this.held.set(key, ref);
        acquirePhoto(item.id, variant).then(
          url => {
            if (this.held && this.held.get(key) === ref) this.$set(this.urls, key, url);
          },
          error => {
            if (!this.held || this.held.get(key) !== ref) return;
            this.held.delete(key); // nothing held for a failed fetch
            if (variant === "full" && this.current && this.current.id === item.id) {
              this.failed = photoErrorMessage(error, "Fotografia nu a putut fi afișată.");
            }
          }
        );
      });
    },
    releaseAll() {
      if (this.held) this.held.forEach(ref => releasePhoto(ref.id, ref.variant));
      this.held = new Map();
      this.urls = {};
    },

    // ---- geometry
    fitOf(item) {
      const known = item.width && item.height ? { w: item.width, h: item.height } : this.dims[item.id];
      const sw = this.stageW || window.innerWidth;
      const sh = this.stageH || window.innerHeight;
      if (!known) return { w: sw, h: sh };
      const k = Math.min(sw / known.w, sh / known.h);
      return { w: Math.round(known.w * k), h: Math.round(known.h * k) };
    },
    onImageLoad(slide, event) {
      const item = slide.item;
      if (item.width && item.height) return;
      const w = event.target.naturalWidth;
      const h = event.target.naturalHeight;
      const old = this.dims[item.id];
      // the full photo has the true ratio; a thumbnail is only a first guess
      if (w && h && (slide.isFull || !old)) this.$set(this.dims, item.id, { w, h });
    },
    local(clientX, clientY) {
      const r = this.$refs.stage.getBoundingClientRect();
      return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
    },
    clampPan() {
      if (!this.current) return;
      const fit = this.fitOf(this.current);
      const maxX = Math.max(0, (fit.w * this.scale - this.stageW) / 2);
      const maxY = Math.max(0, (fit.h * this.scale - this.stageH) / 2);
      this.tx = clamp(this.tx, -maxX, maxX);
      this.ty = clamp(this.ty, -maxY, maxY);
    },
    zoomAt(point, nextScale) {
      const s = clamp(nextScale, 1, MAX_SCALE);
      if (s <= 1.001) {
        this.resetZoom(this.animate);
        return;
      }
      // the image point under `point` stays under it
      this.tx = point.x - ((point.x - this.tx) * s) / this.scale;
      this.ty = point.y - ((point.y - this.ty) * s) / this.scale;
      this.scale = s;
      this.clampPan();
    },
    resetZoom(animated = true) {
      this.withAnimation(animated, () => {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
      });
    },
    withAnimation(animated, fn) {
      this.animate = animated;
      fn();
      if (animated) {
        clearTimeout(this.animTimer);
        this.animTimer = setTimeout(() => (this.animate = false), 240);
      }
    },

    // ---- navigation
    go(step) {
      const next = this.index + step;
      if (next < 0 || next >= this.items.length) return;
      this.index = next;
      this.dragX = 0;
      this.resetZoom(false);
      this.syncHeld();
    },
    slideTo(step) {
      // finish the swipe with an animation, then show the next photo in place
      this.withAnimation(true, () => (this.dragX = -step * this.stageW));
      clearTimeout(this.slideTimer);
      this.slideTimer = setTimeout(() => {
        this.animate = false;
        this.go(step);
      }, 230);
    },
    async download() {
      if (!this.current) return;
      this.downloading = true;
      try {
        await downloadPhoto(this.current.id, `${this.downloadName}-${this.current.id}`);
      } catch (error) {
        this.failed = photoErrorMessage(error, "Fotografia nu a putut fi descărcată.");
      } finally {
        this.downloading = false;
      }
    },

    // ---- gestures (pointer events: mouse, touch and pen alike)
    onPointerDown(e) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (!this.pointers) this.pointers = new Map();
      if (this.$refs.stage.setPointerCapture) {
        try {
          this.$refs.stage.setPointerCapture(e.pointerId);
        } catch (err) {
          // capture is a nicety
        }
      }
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.animate = false;
      if (this.pointers.size === 1) {
        this.gesture = this.singleGesture(e.clientX, e.clientY, false);
      } else if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.gesture = {
          type: "pinch",
          dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          mid: this.local((a.x + b.x) / 2, (a.y + b.y) / 2),
          scale: this.scale,
          tx: this.tx,
          ty: this.ty,
        };
        this.dragX = 0;
      }
    },
    singleGesture(x, y, moved) {
      return { type: this.scale > 1.01 ? "pan" : "swipe", x, y, tx: this.tx, ty: this.ty, t: performance.now(), moved };
    },
    onPointerMove(e) {
      if (!this.pointers || !this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const g = this.gesture;
      if (!g) return;
      if (g.type === "pinch") {
        if (this.pointers.size < 2) return;
        const [a, b] = [...this.pointers.values()];
        const s = clamp((g.scale * Math.hypot(a.x - b.x, a.y - b.y)) / g.dist, 1, MAX_SCALE);
        const mid = this.local((a.x + b.x) / 2, (a.y + b.y) / 2);
        const px = (g.mid.x - g.tx) / g.scale;
        const py = (g.mid.y - g.ty) / g.scale;
        this.scale = s;
        this.tx = mid.x - px * s;
        this.ty = mid.y - py * s;
        this.clampPan();
        return;
      }
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (Math.abs(dx) + Math.abs(dy) > 8) g.moved = true;
      if (g.type === "pan") {
        this.tx = g.tx + dx;
        this.ty = g.ty + dy;
        this.clampPan();
      } else if (this.items.length > 1) {
        const atEdge = (dx > 0 && this.index === 0) || (dx < 0 && this.index === this.items.length - 1);
        this.dragX = atEdge ? dx * 0.3 : dx;
      }
    },
    onPointerUp(e) {
      if (!this.pointers || !this.pointers.has(e.pointerId)) return;
      this.pointers.delete(e.pointerId);
      if (e.pointerType !== "mouse") this.lastTouch = performance.now();
      const g = this.gesture;
      if (g && g.type === "pinch") {
        if (this.pointers.size === 1) {
          const rest = [...this.pointers.values()][0];
          this.gesture = this.singleGesture(rest.x, rest.y, true);
        } else if (!this.pointers.size) {
          this.gesture = null;
        }
        if (this.scale < 1.05) this.resetZoom();
        return;
      }
      if (this.pointers.size) return;
      this.gesture = null;
      if (!g) return;
      if (g.type === "swipe" && this.dragX) {
        const dx = this.dragX;
        const fast = Math.abs(dx) > 30 && Math.abs(dx) / Math.max(1, performance.now() - g.t) > 0.45;
        const step = dx < 0 ? 1 : -1;
        const target = this.index + step;
        if ((Math.abs(dx) > this.stageW * 0.18 || fast) && target >= 0 && target < this.items.length) this.slideTo(step);
        else this.withAnimation(true, () => (this.dragX = 0));
      }
      if (!g.moved && e.pointerType !== "mouse") this.onTap(e);
    },
    onTap(e) {
      const now = performance.now();
      const last = this.lastTap;
      if (last && now - last.t < 320 && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30) {
        this.lastTap = null;
        this.toggleZoom(e.clientX, e.clientY);
      } else {
        this.lastTap = { t: now, x: e.clientX, y: e.clientY };
      }
    },
    onDoubleClick(e) {
      // touch double taps are handled in onTap (dblclick is unreliable there)
      if (this.lastTouch && performance.now() - this.lastTouch < 600) return;
      this.toggleZoom(e.clientX, e.clientY);
    },
    toggleZoom(clientX, clientY) {
      if (this.scale > 1.01) {
        this.resetZoom();
        return;
      }
      this.withAnimation(true, () => this.zoomAt(this.local(clientX, clientY), DOUBLE_TAP_ZOOM));
    },
    onWheel(e) {
      const unit = e.deltaMode === 1 ? 0.05 : e.deltaMode === 2 ? 1 : 0.0015;
      const factor = Math.exp(-e.deltaY * unit);
      this.animate = false;
      this.zoomAt(this.local(e.clientX, e.clientY), this.scale * factor);
    },
  },
};
</script>

<style scoped>
.photo-gallery__strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 2px 6px;
  -webkit-overflow-scrolling: touch;
}
.photo-gallery__thumb {
  flex: 0 0 auto;
  padding: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  cursor: zoom-in;
  transition: box-shadow 0.2s, border-color 0.2s;
}
.photo-gallery__thumb:hover,
.photo-gallery__thumb:focus-visible {
  border-color: #2d8659;
  box-shadow: 0 2px 10px rgba(45, 134, 89, 0.2);
  outline: none;
}

.viewer {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
}
.viewer__bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  padding: 6px 8px;
  padding-top: calc(6px + env(safe-area-inset-top, 0px));
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0));
}
.viewer__count {
  color: white;
  font-size: 15px;
  font-weight: 500;
  margin-left: 4px;
}
.viewer__stage {
  position: relative;
  flex: 1 1 auto;
  overflow: hidden;
  touch-action: none;
  cursor: grab;
}
.viewer__track,
.viewer__slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.viewer__slide {
  display: flex;
  align-items: center;
  justify-content: center;
}
.viewer__slide img {
  display: block;
  max-width: none;
  object-fit: contain;
  transform-origin: center center;
  will-change: transform;
  -webkit-user-drag: none;
  pointer-events: none;
}
.viewer--animate .viewer__track,
.viewer--animate .viewer__slide img {
  transition: transform 0.22s ease-out;
}
.viewer__spinner {
  position: absolute;
}
.viewer__error {
  position: absolute;
  bottom: 15%;
  left: 16px;
  right: 16px;
  text-align: center;
  color: white;
  font-size: 15px;
}
.viewer__nav {
  position: absolute;
  top: 50%;
  margin-top: -20px;
  z-index: 3;
  opacity: 0.85;
}
.viewer__nav--prev {
  left: 12px;
}
.viewer__nav--next {
  right: 12px;
}
@media (hover: none) {
  .viewer__nav {
    display: none;
  }
}
</style>
